import { ToggleButtonGroup } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { CategorySwitcherButton } from "@/features/bookings/components/CategorySwitcher/CategorySwitcherButton";
import {
  rudderAnalytics,
  HotelTrackingEvents,
  FlightTrackingEvents,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";
import { Feature, isFeatureDisabled } from "@/utils/featureFlags";

interface CategorySwitcherProps {
  current: SearchScope;
}

const eventMap = {
  flights: FlightTrackingEvents.USER_SEARCHED_FOR_ORDERS,
  hotels: HotelTrackingEvents.USER_SEARCHED_FOR_ORDERS,
  trips: TripTrackingEvents.USER_SEARCHED_FOR_ORDERS,
} as const;

export const CategorySwitcher = ({ current }: CategorySwitcherProps) => {
  const t = useTranslations();
  const navigate = useNavigate();

  return (
    <ToggleButtonGroup
      value={current}
      exclusive
      onChange={(_, newValue: SearchScope | null) => {
        if (newValue && eventMap[newValue]) {
          rudderAnalytics.track(eventMap[newValue]);
        }
        navigate({
          to: "/bookings",
          search: {
            category: newValue || current,
          },
          replace: true,
        });
      }}
      aria-label="Service"
      sx={{
        display: "flex",
        width: "100%",
        gap: "calc(var(--spacing)*2)",
        padding: "calc(var(--spacing)*2)",
        backgroundColor: "var(--color-surface-neutral-medium)",
        borderRadius: "var(--radius-xl)",
      }}
    >
      <CategorySwitcherButton value="flights">
        {t("flights.title")}
      </CategorySwitcherButton>

      <CategorySwitcherButton value="hotels">
        {t("hotels.title")}
      </CategorySwitcherButton>

      {isFeatureDisabled(Feature.TripsFlow) ? null : (
        <CategorySwitcherButton value="trips">
          {t("trips.title")}
        </CategorySwitcherButton>
      )}
    </ToggleButtonGroup>
  );
};
